import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { RouterOutlet } from '@angular/router';

import {
  AvailableActivationMethods,
  AvailableOperators,
  Cart, Entity, EntityParsed, EntityValueGroup,
  FunnelInfo,
  InternalPromoInfo,
  MacroStepId,
  Offer, OfferExtras,
  OfferPromoInfo,
  StepId
} from 'models';
import { combineLatest, fromEvent, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, first } from 'rxjs/operators';
import {AnalyticsService, OfferPromoService, ScrollService} from 'services';

import { StepsBarItem } from 'ui-kit-lib';
import { SimTypeEnum } from '../../../../../app-shared/models/steps/step-sim.interface';
import { AppStoreFacadeService } from '../../../../facades/app-store-facade.service';
import {OfferExtrasComponent} from '../steps/offer-extras/offer-extras.component';

@Component({
  selector: 'sb-process',
  templateUrl: './process.component.html',
  styleUrls: ['./process.component.scss'],
  // animations: [ routeAnimations ]
})
export class ProcessComponent implements OnInit, OnDestroy {

  shouldShowCart = true;

  simType: SimTypeEnum;

  steps: StepsBarItem[] = [
    {
      id: StepId.FDOIccid,
      label: 'STEP.HEADER.FDO_ICCID'
    },
    {
      id: StepId.Coupon,
      label: 'STEP.HEADER.COUPON'
    },
    {
      id: StepId.Sim,
      label: 'STEP.HEADER.SIM'
    },
    {
      id: StepId.Customer,
      label: 'STEP.HEADER.CUSTOMER'
    },
    {
      id: StepId.Contacts,
      label: 'STEP.HEADER.CONTACTS'
    },
    {
      id: StepId.Consents,
      label: 'STEP.HEADER.CONSENTS'
    },
    {
      id: StepId.Document,
      label: 'STEP.HEADER.DOCUMENT'
    },
    {
      id: StepId.Address,
      label: 'STEP.HEADER.ADDRESS'
    },
    {
      id: StepId.Activation,
      label: 'STEP.HEADER.ACTIVATION'
    },
    {
      id: StepId.ActivationOtp,
      label: 'STEP.HEADER.ACTIVATION_OTP'
    },
    {
      id: StepId.PaymentMethod,
      label: 'STEP.HEADER.PAYMENT_METHOD'
    },
    {
      id: StepId.Payment,
      label: 'STEP.HEADER.PAYMENT'
    },
    {
      id: StepId.OfferExtras,
      label: 'STEP.HEADER.OFFER_EXTRAS'
    },
    {
      id: StepId.Summary,
      label: 'STEP.HEADER.SUMMARY'
    },
    {
      id: StepId.Success,
      label: 'STEP.HEADER.SUCCESS'
    }
  ];

  availableSteps: StepsBarItem[];

  public shouldShowBacklink: boolean;
  public activeStep: StepsBarItem;
  private subscriptions = new Subscription();
  public offer: Offer;
  public offerAlternative?: Offer | any[];
  public offerExtras?: EntityValueGroup | boolean;
  public hasDoublePO: boolean;
  public promos: InternalPromoInfo[];
  public cart: Cart;
  public availableOperators: string[];
  public availableActivationMethods: string[];

  @ViewChild('navButtons', { static: false }) navButtons: ElementRef;

  getYPosition() {
    if (this.getScrollHeight() < this.navButtons.nativeElement.getBoundingClientRect().top) {
      this.scrollService.setSticky(true);
    } else {
      this.scrollService.setSticky(false);
    }
  }
  getScrollHeight(): number {
    const footerHeight = 200;
    const maxHeight = Math.max(
      document.body.scrollHeight, document.documentElement.scrollHeight,
      document.body.offsetHeight, document.documentElement.offsetHeight,
      document.body.clientHeight, document.documentElement.clientHeight
    );
    return maxHeight - footerHeight;
  }

  constructor(private appStoreFacadeService: AppStoreFacadeService,
              private offerPromoService: OfferPromoService,
              private analyticsService: AnalyticsService,
              private scrollService: ScrollService) {
                fromEvent(window, 'scroll').pipe(
                  debounceTime(50),
                  distinctUntilChanged()
                ).subscribe(
                  () =>  this.getYPosition()
                );
              }

  ngOnInit(): void {
    this.setupSteps();
    this.loadStepData();
    this.configureWrappingComponents();
    this.loadCartAndOffer();
    setTimeout(() => {
      this.getYPosition();
    }, 0);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public getRouteAnimation(outlet: RouterOutlet) {
    return outlet && outlet.activatedRouteData && outlet.activatedRouteData.animation;
  }

  private setupSteps() {

    this.appStoreFacadeService.entity.availableSteps$
      .pipe(first(Boolean))
      .subscribe(({ steps }) => {
        this.availableSteps = steps.map((stepId: StepId) => {
          return this.steps.find((step) => step.id === stepId);
        });
      });

    this.subscriptions.add(
      this.appStoreFacadeService.entity.simType$
        .pipe(first(Boolean))
        .subscribe(({ value }) => {
          const simType = value && value.type;
          this.simType = simType;
        })
    );

    this.appStoreFacadeService.route.componentUrlPath$
      .pipe(first(Boolean))
      .subscribe((stepId: StepId) => {
        this.appStoreFacadeService.navigation.setCurrentMacroStepId(MacroStepId.Process);
        this.appStoreFacadeService.navigation.setCurrentStepId(stepId);
      });
  }

  private loadStepData() {
    this.appStoreFacadeService.navigation.currentStepId$
      .pipe(first(Boolean))
      .subscribe((stepId: StepId) => {
        this.appStoreFacadeService.flow.getStepData(stepId);
      });
  }

  private configureWrappingComponents() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.navigation.currentStepId$,
        this.appStoreFacadeService.entity.funnelInfo$
      ])
        .pipe(filter((args) => args.every(Boolean)))
        .subscribe(([currentStepId, funnelInfo]: [StepId, FunnelInfo]) => {
          this.shouldShowBacklink = currentStepId === StepId.Customer;
          this.activeStep = this.steps.find((step) => step.id === currentStepId);
          const hasCoupon = funnelInfo && funnelInfo.hasCoupon;
          this.shouldShowCart = currentStepId === 'sim-type' ? false : this.handleCartToggle(currentStepId, hasCoupon);
          /* this.shouldShowCart = this.handleCartToggle(currentStepId, hasCoupon); */
          this.hasDoublePO = funnelInfo && funnelInfo.isStuOfferDoubleConfigEnabled;
          if (currentStepId === StepId.Customer) {
            this.appStoreFacadeService.entity.simType$
            .pipe(first(Boolean))
            .subscribe(({ value }) => {
              if (value) {
                this.simType = value.type;
              }
            });
          }
        })
    );
  }

  private loadCartAndOffer() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.cart$,
        this.appStoreFacadeService.entity.offer$,
        this.appStoreFacadeService.entity.offerAlternative$,
        this.appStoreFacadeService.entity.availableOperators$,
        this.appStoreFacadeService.entity.availableActivationMethods$,
        this.appStoreFacadeService.entity.offerPromoInfo$,
        this.appStoreFacadeService.entity.couponInfo$,
        this.appStoreFacadeService.entity.offerExtras$,
      ])
        .pipe(filter((args) => args.every(Boolean)))
        .subscribe(([cart, offer, offerAlternative, availableOperators, availableActivationMethods, offerPromoInfo,
                      couponInfo, offerExtras]:
                      [Cart, Offer, Offer, AvailableOperators, AvailableActivationMethods, OfferPromoInfo, Entity, EntityParsed]) => {
          this.cart = cart;
          this.offer = offer;
          this.offerAlternative = (offerAlternative || []);
          this.offerExtras = (offerExtras && offerExtras.value || false);
          this.promos = this.offerPromoService.elaborateOfferPromoInfo(offerPromoInfo, offer, couponInfo);
          this.availableOperators = (availableOperators.operators || []).map(operator => operator.name);
          this.availableActivationMethods = (availableActivationMethods.methods || []);
        })
    );
  }

  public onCartToggle(isOpen) {
    combineLatest([
      this.appStoreFacadeService.store$
    ])
      .pipe(first(([ store ]) => Boolean(store)))
      .subscribe(([ store ]) => {
        this.analyticsService.sendToggleCartAnalytics(isOpen, store);
      });

    /* if (isOpen) {
      this.appStoreFacadeService.entity.simType$
      .pipe(first(Boolean))
      .subscribe(({ value }) => {
        if (value) {
          this.simType = value.type;
        }
      });
    } */
  }

  private handleCartToggle(stepId, hasCoupon = false) {
    switch (stepId) {
      case StepId.Winback:
        return false;
      case StepId.Coupon:
        return !hasCoupon;
      default:
        return true;
    }
  }
}
